import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Stack,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { SiteAssignmentDto } from '../api/userSiteAssignmentsApi';
import { What3WordsLink } from '../../../shared/components/atoms/What3WordsLink';

interface AssignedSiteCardProps {
  assignment: SiteAssignmentDto;
  onRemoveClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    siteId: number
  ) => void;
  isRemoving?: boolean;
  canManage: boolean;
}

export const AssignedSiteCard: React.FC<AssignedSiteCardProps> = ({
  assignment,
  onRemoveClick,
  isRemoving = false,
  canManage,
}) => {
  const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isRemoving) {
      onRemoveClick(event, assignment.siteId);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        opacity: isRemoving ? 0.5 : 1,
        transition: 'opacity 0.2s',
      }}
      data-testid="assigned-site-card"
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <LocationIcon
            sx={{ color: 'text.secondary', fontSize: 20, mt: 0.25 }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              data-testid="site-name"
            >
              {assignment.siteName}
            </Typography>
            {assignment.what3WordsAddress && (
              <What3WordsLink
                address={assignment.what3WordsAddress}
                variant="caption"
                testId="site-w3w-address"
              />
            )}
          </Box>
          {canManage && (
            <IconButton
              size="small"
              onClick={handleRemoveClick}
              disabled={isRemoving}
              data-testid="remove-site-button"
              sx={{
                p: 0.5,
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
